import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { KeyCategory, KeyCategoryCreate } from "../utils/helpers/types";
import Table, { Column } from "../components/table/Table";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";
import {
  createKeyCategory,
  getKeyCategories,
  updateKeyCategory
} from "../utils/helpers/data/key-categories";
import Button from "../components/button/Button";
import Modal from "../components/modal/Modal";
import Input from "../components/input/Input";
import { descriptionRender } from "../utils/helpers/render";

const KeyCategoriesPage: FunctionComponent = () => {
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [dataSource, setDataSource] = useState<KeyCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [categoryOnEdit, setCategoryOnEdit] = useState<KeyCategory | null>(
    null
  );

  const displayAddEditModal = (category: KeyCategory | null) => {
    setCategoryOnEdit(category);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setCategoryOnEdit(null);
    setShowAddEditModal(false);
  };

  const { notify } = useContext(LayoutContext);

  const fetchKeyCategorys = async (pageNumber = pagination.pageNumber) => {
    setLoading(true);
    const { error, message, data } = await getKeyCategories(pagination);
    setLoading(false);
    if (error || !data) {
      notify("error", `Unable to fetch key information records: ${message}`);
      return;
    }

    setDataSource(data.categories);
    setPagination({ ...pagination, total: data.count, pageNumber });
  };

  const columns: Column<KeyCategory>[] = [
    {
      title: "Key ID",
      dataIndex: "id",
      key: "id"
    },
    {
      title: "Category Name",
      dataIndex: "keyCategory",
      key: "keyCategory"
    },
    {
      title: "Category Type",
      dataIndex: "keyType",
      key: "keyType"
    },
    {
      title: "Category Name",
      dataIndex: "description",
      key: "description",
      render: descriptionRender
    },
    {
      title: "",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (_, category) => (
        <Button
          type="accent"
          size="small"
          onClick={() => displayAddEditModal(category)}
        >
          UPDATE
        </Button>
      )
    }
  ];

  useEffect(() => {
    fetchKeyCategorys();
  }, [pagination.pageNumber, pagination.pageSize]);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Des Key Information</h1>
      <div className="flex end vertical-margin spaced">
        <Button onClick={() => displayAddEditModal(null)}>
          New Des Key Info
        </Button>
      </div>

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onPageChange={pageNumber =>
          setPagination({ ...pagination, pageNumber })
        }
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
      />

      <AddEditModal
        visible={showAddEditModal}
        cancel={() => dismissAddEditModal()}
        onComplete={stayOnPage =>
          fetchKeyCategorys(stayOnPage ? pagination.pageNumber : 1)
        }
        categoryOnEdit={categoryOnEdit}
      />
    </main>
  );
};

interface AddEditProps {
  visible: boolean;
  cancel: () => void;
  onComplete: (stayOnPage: boolean) => void;
  categoryOnEdit: KeyCategory | null;
}

const blankKeyCategory: KeyCategoryCreate = {
  description: "",
  keyCategory: "",
  keyType: "",
  keyTypeCode: ""
};

const AddEditModal: FunctionComponent<AddEditProps> = ({
  visible,
  cancel,
  onComplete,
  categoryOnEdit
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ ...blankKeyCategory });

  const handleChange = (key: keyof KeyCategoryCreate, value: unknown) => {
    setFormData({ ...formData, [key]: value });
  };

  const { notify } = useContext(LayoutContext);

  useEffect(() => {
    setFormData({ ...blankKeyCategory, ...(categoryOnEdit || {}) });
  }, [visible, categoryOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const request = categoryOnEdit
      ? updateKeyCategory({ ...categoryOnEdit, ...formData })
      : createKeyCategory({
          ...formData
        });
    setLoading(true);
    const { error, message } = await request;
    setLoading(false);
    if (error) {
      notify(
        "error",
        `Unable to ${
          categoryOnEdit ? "update" : "create"
        } key category: ${message}`
      );
    } else {
      notify(
        "success",
        `Key information ${categoryOnEdit ? "updated" : "created"} successfully`
      );
      onComplete(false);
      cancel();
    }
  };

  return (
    <Modal visible={visible} cancel={cancel} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Category name</span>
          <Input
            value={formData.keyCategory}
            onChange={value => handleChange("keyCategory", value)}
            inputValidation={{
              transform: value => value.replace(/\s/g, "").toUpperCase()
            }}
            placeholder="Enter category name"
            required
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Key Type</span>
          <Input
            value={formData.keyType}
            onChange={value => handleChange("keyType", value)}
            placeholder="Enter key type"
            required
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Key Type Code</span>
          <Input
            value={formData.keyTypeCode}
            onChange={value => handleChange("keyTypeCode", value)}
            placeholder="Enter key type code"
            inputValidation={{
              transform: value => value.toUpperCase().replace(/[^\da-f]/gi, "")
            }}
            required
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Key Description</span>
          <Input
            value={formData.description}
            onChange={value => handleChange("description", value)}
            placeholder="Enter description"
            required
            responsive
          />
        </div>

        <div className="vertical-margin flex end spaced">
          <Button type="accent" onClick={cancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            {categoryOnEdit ? "Update" : "Create new"} info
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default KeyCategoriesPage;
